import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { TokenService } from "@/gen/omnios/v1/token_connect.js";
import type { ApiToken } from "@/gen/omnios/v1/token_pb.js";
import { transport } from "@/lib/grpc";
import { SCOPE_GROUPS } from "@/lib/scopes";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Copy, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/tokens")({
	component: TokensSettingsPage,
});

const client = createClient(TokenService, transport);

function formatDate(ts: { seconds: bigint } | undefined): string {
	if (!ts) return "—";
	return new Date(Number(ts.seconds) * 1000).toLocaleDateString("ru-RU", {
		day: "numeric",
		month: "short",
		year: "numeric",
	});
}

function isExpired(ts: { seconds: bigint } | undefined): boolean {
	if (!ts) return false;
	return Number(ts.seconds) * 1000 < Date.now();
}

function TokensSettingsPage() {
	const { t } = useTranslation("settings");

	function relativeTime(ts: { seconds: bigint } | undefined): string {
		if (!ts) return t("tokens_relative_never");
		const diff = Date.now() - Number(ts.seconds) * 1000;
		if (diff < 60_000) return t("tokens_relative_just_now");
		if (diff < 3_600_000)
			return t("tokens_relative_min_ago", { count: Math.floor(diff / 60_000) });
		if (diff < 86_400_000)
			return t("tokens_relative_hours_ago", {
				count: Math.floor(diff / 3_600_000),
			});
		return t("tokens_relative_days_ago", {
			count: Math.floor(diff / 86_400_000),
		});
	}

	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: t("tokens_breadcrumb") },
	]);

	const [tokens, setTokens] = useState<ApiToken[]>([]);
	const [loading, setLoading] = useState(true);
	const [showCreate, setShowCreate] = useState(false);

	const [search, setSearch] = useState("");
	const [revokeId, setRevokeId] = useState<string | null>(null);
	const [createdPlaintext, setCreatedPlaintext] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);

	const [name, setName] = useState("");
	const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set());
	const [expiryDays, setExpiryDays] = useState("90");

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const res = await client.listPersonalTokens({});
			setTokens(res.tokens);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const toggleScope = (scope: string) => {
		setSelectedScopes((prev) => {
			const next = new Set(prev);
			if (next.has(scope)) next.delete(scope);
			else next.add(scope);
			return next;
		});
	};

	const handleCreate = async () => {
		const res = await client.createPersonalToken({
			name: name.trim(),
			scopes: [...selectedScopes],
			expiresInDays: Number.parseInt(expiryDays),
		});
		setCreatedPlaintext(res.plaintext);
		setShowCreate(false);
		setName("");
		setSelectedScopes(new Set());
		setExpiryDays("90");
		load();
	};

	const handleRevoke = async () => {
		if (!revokeId) return;
		await client.revokeToken({ tokenId: revokeId });
		setRevokeId(null);
		load();
	};

	const handleCopy = async () => {
		if (!createdPlaintext) return;
		await navigator.clipboard.writeText(createdPlaintext);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	const matchesSearch = (tk: ApiToken) =>
		!search || tk.name.toLowerCase().includes(search.toLowerCase());

	const activeTokens = tokens.filter(
		(tk) => !tk.revokedAt && !isExpired(tk.expiresAt) && matchesSearch(tk),
	);
	const inactiveTokens = tokens.filter(
		(tk) => (tk.revokedAt || isExpired(tk.expiresAt)) && matchesSearch(tk),
	);

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between gap-4">
				<div className="relative">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("tokens_search_placeholder")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-9 h-9 w-56"
					/>
				</div>
				<Button size="sm" onClick={() => setShowCreate(true)}>
					<Plus className="h-3.5 w-3.5 mr-1.5" />
					{t("tokens_create_token")}
				</Button>
			</div>

			{loading && (
				<div className="flex items-center justify-center py-12">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			)}

			{!loading && activeTokens.length === 0 && inactiveTokens.length === 0 && (
				<Card className="px-4 py-8 text-center">
					<p className="text-sm text-muted-foreground">{t("tokens_empty")}</p>
				</Card>
			)}

			{activeTokens.map((tk) => (
				<Card key={tk.id} className="px-4 py-4">
					<div className="flex items-start justify-between gap-3">
						<div className="flex-1 min-w-0 space-y-1">
							<div className="flex items-center gap-2">
								<h3 className="text-sm font-medium truncate">{tk.name}</h3>
								<span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium font-mono text-muted-foreground">
									{tk.tokenPrefix}...
								</span>
							</div>
							<div className="flex gap-3 text-xs text-muted-foreground">
								<span>
									{t("tokens_created", { date: formatDate(tk.createdAt) })}
								</span>
								<span>
									{t("tokens_expires", { date: formatDate(tk.expiresAt) })}
								</span>
								<span>
									{t("tokens_used", { time: relativeTime(tk.lastUsedAt) })}
								</span>
							</div>
							{tk.scopes.length > 0 && (
								<div className="flex flex-wrap gap-1 pt-0.5">
									{tk.scopes.map((s) => (
										<Badge key={s} variant="secondary" className="text-[10px]">
											{s}
										</Badge>
									))}
								</div>
							)}
						</div>
						<Button
							variant="ghost"
							size="icon"
							className="h-7 w-7 text-destructive hover:text-destructive"
							title={t("tokens_revoke_title_button")}
							onClick={() => setRevokeId(tk.id)}
						>
							<Trash2 className="h-3.5 w-3.5" />
						</Button>
					</div>
				</Card>
			))}

			{inactiveTokens.length > 0 && (
				<>
					<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider pt-2">
						{t("tokens_inactive")}
					</p>
					{inactiveTokens.map((tk) => (
						<Card key={tk.id} className="px-4 py-3 opacity-50">
							<div className="flex items-center gap-2">
								<span className="text-sm font-medium truncate">{tk.name}</span>
								<Badge variant="outline" className="text-[10px]">
									{tk.revokedAt ? t("tokens_revoked") : t("tokens_expired")}
								</Badge>
								<span className="text-xs text-muted-foreground ml-auto">
									{formatDate(tk.createdAt)}
								</span>
							</div>
						</Card>
					))}
				</>
			)}

			{/* Create dialog */}
			<Dialog open={showCreate} onOpenChange={setShowCreate}>
				<DialogContent className="sm:max-w-lg">
					<DialogHeader>
						<DialogTitle>{t("tokens_create_title")}</DialogTitle>
						<DialogDescription>{t("tokens_create_desc")}</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-2">
						<div className="space-y-1.5">
							<Label>{t("tokens_label_name")}</Label>
							<Input
								placeholder={t("tokens_name_placeholder")}
								value={name}
								onChange={(e) => setName(e.target.value)}
							/>
						</div>
						<div className="space-y-1.5">
							<Label>{t("tokens_label_expiry")}</Label>
							<Select value={expiryDays} onValueChange={setExpiryDays}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="7">{t("tokens_expiry_7")}</SelectItem>
									<SelectItem value="30">{t("tokens_expiry_30")}</SelectItem>
									<SelectItem value="90">{t("tokens_expiry_90")}</SelectItem>
									<SelectItem value="180">{t("tokens_expiry_180")}</SelectItem>
									<SelectItem value="365">{t("tokens_expiry_365")}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label>{t("tokens_label_permissions")}</Label>
							<div className="max-h-60 space-y-3 overflow-y-auto rounded-md border p-3">
								{SCOPE_GROUPS.map((group) => (
									<div key={group.resource}>
										<p className="text-xs font-medium text-muted-foreground mb-1">
											{group.label}
										</p>
										<div className="flex flex-wrap gap-x-4 gap-y-1">
											{group.actions.map((a) => {
												const scope = `${group.resource}:${a.action}`;
												return (
													<label
														key={scope}
														className="flex items-center gap-1.5 text-sm cursor-pointer"
													>
														<Checkbox
															checked={selectedScopes.has(scope)}
															onCheckedChange={() => toggleScope(scope)}
														/>
														{a.label}
													</label>
												);
											})}
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setShowCreate(false)}
						>
							{t("common:cancel")}
						</Button>
						<Button size="sm" onClick={handleCreate} disabled={!name.trim()}>
							{t("common:create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Plaintext show-once dialog */}
			<Dialog
				open={!!createdPlaintext}
				onOpenChange={(o) => {
					if (!o) setCreatedPlaintext(null);
				}}
			>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>{t("tokens_created_title")}</DialogTitle>
						<DialogDescription>{t("tokens_created_desc")}</DialogDescription>
					</DialogHeader>
					<div className="space-y-1.5">
						<div className="flex gap-2">
							<Input
								readOnly
								value={createdPlaintext ?? ""}
								className="font-mono text-xs"
							/>
							<Button variant="outline" size="icon" onClick={handleCopy}>
								{copied ? (
									<Check className="h-3.5 w-3.5 text-green-600" />
								) : (
									<Copy className="h-3.5 w-3.5" />
								)}
							</Button>
						</div>
						{copied && (
							<p className="text-xs text-muted-foreground">
								{t("tokens_copied")}
							</p>
						)}
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setCreatedPlaintext(null)}
						>
							{t("tokens_done")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Revoke confirm */}
			<AlertDialog
				open={!!revokeId}
				onOpenChange={(o) => {
					if (!o) setRevokeId(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("tokens_revoke_title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("tokens_revoke_desc")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleRevoke}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("tokens_revoke_action")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
